import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Arrow, CheckeredFlag } from "@/components/site/parts";
import { useCopy } from "@/lib/copy";

// A survey is data: steps, each with one or more fields.
// Field types: single, multi, scale (1-5), text, email, checkbox.

const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || v === false;

export default function Survey() {
  const t = useCopy();
  const { slug } = useParams();
  const [survey, setSurvey] = useState(undefined);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setSurvey(undefined);
    api
      .get(slug ? `/surveys/by-slug/${slug}` : "/surveys/current")
      .then(({ data }) => setSurvey(data))
      .catch(() => setSurvey(null));
  }, [slug]);

  if (survey === undefined) {
    return <section className="bx" style={{ paddingTop: 32 }}><p className="kicker">Loading survey…</p></section>;
  }
  if (survey === null) return <NoSurvey />;

  const steps = survey.steps;
  const total = steps.length;
  const cur = steps[Math.min(step, total - 1)];
  const optional = cur.fields.every((f) => !f.required);
  const pct = done ? 100 : (step / total) * 100;

  const set = (id, v) => {
    setError("");
    setAnswers((a) => ({ ...a, [id]: v }));
  };

  const missing = cur.fields.find((f) => f.required && isEmpty(answers[f.id]));

  const next = async () => {
    if (missing) {
      setError(`Pick an answer to continue${missing.label ? `: ${missing.label}` : ""}.`);
      return;
    }
    if (step < total - 1) {
      setStep(step + 1);
      setError("");
      return;
    }
    setSending(true);
    try {
      await api.post(`/surveys/by-slug/${survey.slug}/responses`, { answers });
      setDone(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
    setError("");
  };

  return (
    <>
      <section className="bx" style={{ paddingTop: 16 }}>
        <div className="ph rise d1 padl" style={{ padding: "40px 48px" }}>
          <span className="mono" style={{ fontSize: 12, color: "#A3A8B4", textTransform: "uppercase" }}>{t("survey.kicker")} · {survey.slug}</span>
          <h1 className="ph-h ph-h-sm stretch" style={{ margin: "12px 0 0", animationDelay: ".2s" }}>{survey.title}</h1>
          {survey.intro && (
            <p className="ph-sub" style={{ marginTop: 16, maxWidth: 640, fontSize: 17 }}>
              {survey.intro} {total} quick {total === 1 ? "step" : "steps"}.
            </p>
          )}
          <div className="survey-progress" role="progressbar" aria-label="Survey progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done ? total : step}>
            <div className="survey-progress-rail" />
            <div className="survey-progress-fill" style={{ width: `${pct}%` }} />
            {steps.map((_, i) => (
              <span key={i} className="mono survey-progress-mark" style={{ left: `${(i + 0.5) * (100 / total)}%`, color: done || i <= step ? "#FFC83D" : "#6E7380" }}>
                <span className="hide-m">STEP </span>{i + 1}
              </span>
            ))}
            <svg aria-hidden="true" width="44" height="22" viewBox="0 0 44 22" className="survey-progress-car" style={{ left: `calc(${pct}% - ${0.44 * pct}px)` }}>
              <rect x="2" y="5" width="38" height="12" rx="5" style={{ fill: "var(--blue)" }} />
              <rect x="20" y="3" width="10" height="16" rx="3" fill="#15171C" />
              <circle cx="10" cy="19" r="3" fill="#0B0C0F" /><circle cx="32" cy="19" r="3" fill="#0B0C0F" />
              <circle cx="10" cy="3" r="3" fill="#0B0C0F" /><circle cx="32" cy="3" r="3" fill="#0B0C0F" />
            </svg>
            <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" style={{ position: "absolute", right: -4, top: 10 }}>
              <g fill="#FFFFFF"><rect width="8" height="8" /><rect x="8" y="8" width="8" height="8" /><rect x="16" width="8" height="8" /><rect y="16" width="8" height="8" /><rect x="16" y="16" width="8" height="8" /></g>
            </svg>
          </div>
        </div>
      </section>

      <section className="bx" style={{ paddingTop: 16 }}>
        <div className="survey-card padl">
          {done ? (
            <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 18 }}>
              <CheckeredFlag />
              <span className="kicker">{t("survey.done_kicker")}</span>
              <h2 className="disp" style={{ margin: 0, fontSize: 44, lineHeight: 0.95, fontWeight: 900, textTransform: "uppercase" }}>{t("survey.done_title")}</h2>
              <p className="lede">{survey.thanks}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <Link to="/blog" className="btn btn-ink" style={{ height: 52, borderRadius: 14 }}>Read the {t("nav.blog").toLowerCase()} <Arrow /></Link>
                <button type="button" className="btn btn-line" style={{ height: 52, borderRadius: 14, background: "#fff" }} onClick={restart}>Take it again</button>
              </div>
            </div>
          ) : (
            <div key={step} className="survey-step-in" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <span className="kicker">STEP {step + 1} OF {total}</span>
                <span className="kicker">{optional ? "OPTIONAL" : ""}</span>
              </div>
              <h2 id="survey-question" className="disp survey-question">{cur.title}</h2>
              {cur.hint && <p style={{ margin: "-10px 0 0", fontSize: 15, color: "#4A4F5A" }}>{cur.hint}</p>}
              {cur.fields.map((f) => (
                <Field key={f.id} field={f} value={answers[f.id]} onChange={(v) => set(f.id, v)} only={cur.fields.length === 1} />
              ))}
              {error && <p role="alert" style={{ margin: 0, color: "#C42B2B", fontWeight: 600 }}>{error}</p>}
              <div className="survey-nav">
                <button type="button" className="btn btn-line" style={{ height: 52, borderRadius: 14, background: "#fff" }} disabled={step === 0} onClick={() => { setStep(step - 1); setError(""); }}>
                  ← Back
                </button>
                <button type="button" className="btn btn-ink" style={{ height: 52, borderRadius: 14, fontSize: 16, boxShadow: "0 5px 0 #0B0C0F" }} disabled={sending} onClick={next}>
                  {step === total - 1 ? (sending ? "Submitting…" : "Submit") : "Next"} <Arrow />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Field({ field, value, onChange, only }) {
  const label = field.label && !only ? field.label : null;
  const aria = field.label || "Answer";
  switch (field.type) {
    case "single": {
      const rich = field.options.some((o) => o.desc);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {label && <span className="lbl">{label}</span>}
          <div role="radiogroup" aria-labelledby={label ? undefined : "survey-question"} aria-label={label ? aria : undefined} className={rich ? "opt-grid opt-grid-rich" : "opt-grid"}>
            {field.options.map((o, i) => {
              const on = value === o.value;
              return (
                <button key={o.value} type="button" role="radio" aria-checked={on} className={`opt survey-option${on ? " on" : ""}${rich ? " rich" : ""}`} onClick={() => onChange(o.value)}>
                  {rich && <span className="mono" style={{ fontSize: 11, opacity: 0.8 }}>OPTION {String.fromCharCode(65 + i)}</span>}
                  <span className={rich ? "disp" : undefined} style={rich ? { fontSize: 20, fontWeight: 800, textTransform: "uppercase" } : undefined}>{o.label}</span>
                  {o.desc && <span style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.85 }}>{o.desc}</span>}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    case "multi": {
      const list = Array.isArray(value) ? value : [];
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {label && <span className="lbl">{label}</span>}
          <div role="group" aria-label={aria} style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {field.options.map((o) => {
              const on = list.includes(o.value);
              return (
                <button key={o.value} type="button" role="checkbox" aria-checked={on} className={`opt survey-chip${on ? " on" : ""}`}
                  onClick={() => onChange(on ? list.filter((v) => v !== o.value) : [...list, o.value])}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    case "scale":
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {label && <span className="lbl">{label}</span>}
          <div role="radiogroup" aria-label={aria} className="survey-scale">
            {[1, 2, 3, 4, 5].map((n) => {
              const lit = value != null && n <= value;
              return (
                <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`Level ${n} of 5`} className="opt survey-scale-option"
                  style={{ height: 56 + n * 18, background: lit ? (n === 5 ? "#FF3D3D" : "#FFC83D") : "#fff", boxShadow: value === n ? "4px 4px 0 #15171C" : "none" }}
                  onClick={() => onChange(n)}>
                  <span className="disp" style={{ fontSize: 24, fontWeight: 900 }}>{n}</span>
                </button>
              );
            })}
          </div>
          <div className="mono" style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, color: "#6B6A63" }}>
            <span>1 · {(field.min_label || "Low").toUpperCase()}</span>
            <span>5 · {(field.max_label || "High").toUpperCase()}</span>
          </div>
        </div>
      );
    case "checkbox":
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15 }}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} style={{ width: 20, height: 20, accentColor: "#15171C" }} />
          {field.label}
        </label>
      );
    case "email":
      return (
        <label className="lbl">
          {field.label}{!field.required && " (optional)"}
          <input className="fld" type="email" autoComplete="email" value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
    default:
      return (
        <label className="lbl">
          {field.label}{!field.required && " (optional)"}
          <textarea className="fld" rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
  }
}

function NoSurvey() {
  const t = useCopy();
  return (
    <section className="bx" style={{ paddingTop: 16 }}>
      <div className="ph rise d1 padl">
        <span className="mono" style={{ fontSize: 12, color: "#A3A8B4", textTransform: "uppercase" }}>{t("survey.kicker")}</span>
        <h1 className="ph-h stretch" style={{ margin: "16px 0", animationDelay: ".2s" }}>
          {t("survey.closed_title")} <span className="em-y">{t("survey.closed_accent")}</span>
        </h1>
        <p className="ph-sub">{t("survey.closed_body")}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 }}>
          <Link to="/projects" className="btn btn-y" style={{ height: 52, borderRadius: 14 }}>{t("nav.projects")} <Arrow /></Link>
          <Link to="/contact" className="btn" style={{ height: 52, borderRadius: 14, border: "2px solid #4A4F5A", color: "#fff", background: "transparent" }}>{t("nav.contact")}</Link>
        </div>
      </div>
    </section>
  );
}
